<template>
  <div class="p-6">
    <h2 class="text-2xl font-bold mb-4 text-green-700">
      📢 โปรโมชัน <span v-if="selectedParentTitle"> / {{ selectedParentTitle }}</span>
    </h2>
    <button
      @click="openAddModal"
      class="mb-4 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
    >
      ➕ เพิ่มโปรโมชัน
    </button>

    <p v-if="showingChildItems" class="text-sm text-gray-500 mb-2">
      ✏️ เพิ่มรายการลูกสำหรับ: <span class="font-semibold text-green-600">{{ selectedParentTitle }}</span>
    </p>


    <div class="overflow-x-auto">
      <table class="min-w-full border border-gray-300">
        <thead class="bg-green-200 text-gray-800 text-sm">
          <tr>
            <th class="px-4 py-2">หัวข้อหลัก</th>
            <th class="px-4 py-2">หัวข้อรอง</th>
            <th class="px-4 py-2">รูปภาพ</th>
            <th class="px-4 py-2">รายละเอียด</th>
            <th class="px-4 py-2 text-red-600">ราคาเต็ม</th>
            <th class="px-4 py-2 text-green-600">ราคาขาย</th>
            <th class="px-4 py-2">Ranking</th>
            <th class="px-4 py-2">Action</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="!showingChildItems">
            <tr v-for="promo in mainPromos" :key="promo.promo_id" class="text-sm border-t">
              <td class="px-4 py-2">{{ promo.title }}</td>
              <td class="px-4 py-2">{{ promo.subtitle }}</td>
              <td class="px-4 py-2">
                <img :src="promo.image" class="w-16 h-10 rounded object-cover" v-if="promo.image" />
                <span v-else class="text-gray-400 italic">-</span>
              </td>
              <td class="px-4 py-2">{{ promo.detail }}</td>
              <td class="px-4 py-2 text-red-600">{{ promo.full_price }}</td>
              <td class="px-4 py-2 text-green-600">{{ promo.selling_price }}</td>
              <td class="px-4 py-2">{{ promo.ranking }}</td>
              <td class="px-4 py-2 space-x-1">
                <button @click="editPromoClick(promo)" class="text-blue-600 text-xs hover:underline">✏ Edit</button>
                <button @click="deletePromo(promo.promo_id)" class="text-red-600 text-xs hover:underline">🗑 Remove</button>
                <button @click="showOnlyChildItems(promo.promo_id)" class="text-gray-700 text-xs hover:underline">
                  📂 Show item
                </button>
              </td>
            </tr>
          </template>

          <template v-else>
            <tr v-for="child in childPromosOf(selectedParentId)" :key="'child-' + child.promo_id" class="text-sm bg-gray-50">
              <td class="px-4 py-2 pl-6">↪️ {{ child.title }}</td>
              <td class="px-4 py-2">{{ child.subtitle }}</td>
              <td class="px-4 py-2">
                <img :src="child.image" class="w-16 h-10 rounded object-cover" v-if="child.image" />
                <span v-else class="text-gray-400 italic">-</span>
              </td>
              <td class="px-4 py-2">{{ child.detail }}</td>
              <td class="px-4 py-2 text-red-600">{{ child.full_price }}</td>
              <td class="px-4 py-2 text-green-600">{{ child.selling_price }}</td>
              <td class="px-4 py-2">{{ child.ranking }}</td>
              <td class="px-4 py-2 space-x-1">
                <button @click="editPromoClick(child)" class="text-blue-600 text-xs hover:underline">✏ Edit</button>
                <button @click="deletePromo(child.promo_id)" class="text-red-600 text-xs hover:underline">🗑 Remove</button>
                <button @click="backToParentList" class="text-gray-700 text-xs hover:underline">🔙 Back</button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <!-- Modal รวมสำหรับเพิ่ม/แก้ไข -->
      <div
        v-if="showModal"
        class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
        @click.self="closeModal"
      >
        <div class="bg-white p-6 rounded-lg shadow w-full max-w-md">
          <h2 class="text-center text-xl font-bold text-gray-800 mb-6">
            {{ isEditing ? ' แก้ไขโปรโมชัน' : ' เพิ่มโปรโมชัน' }}
          </h2>

          <div class="space-y-3">
            <div>
              <label class="block text-sm text-gray-500 mb-1">หัวข้อหลัก</label>
              <input v-model="currentPromo.title" class="w-full border px-4 py-2 rounded bg-gray-100" />
            </div>

            <div>
              <label class="block text-sm text-gray-500 mb-1">หัวข้อรอง</label>
              <input v-model="currentPromo.subtitle" class="w-full border px-4 py-2 rounded bg-gray-100" />
            </div>

            <div>
              <label class="block text-sm text-gray-500 mb-1">URL รูปภาพ หรือชื่อไฟล์</label>
              <input v-model="currentPromo.image" class="w-full border px-4 py-2 rounded bg-gray-100" />
            </div>

            <div>
              <label class="block text-sm text-gray-500 mb-1">รายละเอียดโปรโมชัน</label>
              <input v-model="currentPromo.detail" class="w-full border px-4 py-2 rounded bg-gray-100" />
            </div>

            <div>
              <label class="block text-sm text-gray-500 mb-1">Ranking</label>
              <input
                type="number"
                v-model="currentPromo.ranking"
                inputmode="numeric"
                pattern="[0-9]*"
                class="w-full border px-4 py-2 rounded bg-gray-100"
              />
            </div>

            <div class="flex gap-2">
              <div class="w-1/2">
                <label class="block text-sm text-gray-500 mb-1">ราคาเต็ม</label>
                <input
                  type="number"
                  v-model="currentPromo.full_price"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  class="w-full border px-4 py-2 rounded bg-gray-100"
                />
              </div>
              <div class="w-1/2">
                <label class="block text-sm text-gray-500 mb-1">ราคาขาย</label>
                <input
                  type="number"
                  v-model="currentPromo.selling_price"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  class="w-full border px-4 py-2 rounded bg-gray-100"
                />
              </div>
            </div>

            <div class="flex justify-center">
              <label class="inline-flex items-center space-x-2">
                <input type="checkbox" v-model="currentPromo.is_active" />
                <span class="text-sm text-gray-600">เปิดใช้งานโปรโมชัน</span>
              </label>
            </div>
          </div>

          <div class="mt-6 text-center">
            <button
              @click="submitPromo"
              class="bg-green-500 text-white px-6 py-2 rounded-full hover:bg-green-600 text-lg"
            >
              ยืนยัน
            </button>
          </div>
        </div>
      </div>


    <!-- Modal ยืนยันการลบ -->
    <div
      v-if="showDeleteConfirm"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-yellow-100 border border-yellow-300 rounded-lg p-6 text-center max-w-sm shadow-md">
        <p class="text-gray-800 text-lg font-semibold mb-4">
          คุณต้องการลบ "{{ promoToDelete?.title }}" ใช่หรือไม่ ?
        </p>
        <div class="flex justify-center gap-4">
          <button
            @click="confirmDelete"
            class="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-6 rounded-full"
          >
            ใช่
          </button>
          <button
            @click="cancelDelete"
            class="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-6 rounded-full"
          >
            ไม่ใช่
          </button>
        </div>
      </div>
    </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'PromoPage',
  data() {
    return {
      promos: [],
      showModal: false,
      isEditing: false,
      showDeleteConfirm: false,
      promoToDelete: null,
      selectedParentTitle: null, // ✅ เพิ่มตัวนี้ไว้เก็บชื่อโปรโมชันแม่ที่เลือก
      currentPromo: {
        title: '', subtitle: '', image: '', detail: '',
        ranking: 1, full_price: 0, selling_price: 0,
        is_active: true, parent_id: null
      },
      showingChildItems: false, // ✅ เพิ่มสำหรับ toggle ระหว่างโปรโมชันหลักกับลูก
      selectedParentId: null    // ✅ เก็บ parent_id ที่เลือก
    };
  },
  computed: {
    mainPromos() {
      return this.promos.filter(p => p.parent_id === null);
    },
  },
  mounted() {
    this.fetchPromos();
  },
  methods: {
    async fetchPromos() {
      try {
        const res = await fetch('/api/promos');
        this.promos = await res.json();
      } catch (err) {
        console.error('โหลดโปรโมชันล้มเหลว', err);
      }
    },
    openAddModal() {
      this.currentPromo = {
        title: '', subtitle: '', image: '', detail: '',
        ranking: 1, full_price: 0, selling_price: 0,
        is_active: true,
        parent_id: this.showingChildItems ? this.selectedParentId : null  // ✅ ถ้าอยู่ใน child ก็ใส่ parent ให้เลย
      };
      this.isEditing = false;
      this.showModal = true;
    },
    editPromoClick(promo) {
      this.currentPromo = {
        ...promo,
        is_active: promo.is_active === 1, // ✅ แปลง 1/0 ให้เป็น true/false
      };
      this.isEditing = true;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async submitPromo() {
      try {
        const method = this.isEditing ? 'PUT' : 'POST';
        const url = this.isEditing ? `/api/promos/${this.currentPromo.promo_id}` : '/api/promos';
        await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.currentPromo),
        });
        this.showModal = false;
        this.fetchPromos();
      } catch (err) {
        console.error('❌ บันทึกโปรโมชันล้มเหลว:', err);
      }
    },
    deletePromo(promoId) {
      const promo = this.promos.find(p => p.promo_id === promoId);
      this.promoToDelete = promo;
      this.showDeleteConfirm = true;
    },
    async confirmDelete() {
      try {
        const res = await fetch(`/api/promos/${this.promoToDelete.promo_id}`, {
          method: 'DELETE',
        });
        const data = await res.json();
        if (data.success) {
          this.showDeleteConfirm = false;
          this.promoToDelete = null;
          await this.fetchPromos();
        } else {
          alert(data.message || "ลบไม่สำเร็จ");
        }
      } catch (err) {
        console.error("❌ ลบโปรโมชันล้มเหลว:", err);
        alert("เกิดข้อผิดพลาดในการลบ");
      }
    },
    cancelDelete() {
      this.showDeleteConfirm = false;
      this.promoToDelete = null;
    },
    showOnlyChildItems(parentId) {
      const parentPromo = this.promos.find(p => p.promo_id === parentId);
      this.selectedParentTitle = parentPromo?.title || null; // ✅ เพิ่มบรรทัดนี้
      this.selectedParentId = parentId;
      this.showingChildItems = true;
    },
    backToParentList() {
      this.selectedParentId = null;
      this.showingChildItems = false;
      this.selectedParentTitle = null; // ✅ เคลียร์ชื่อหัวข้อ
    },
    childPromosOf(parentId) {
      return this.promos.filter(p => p.parent_id === parentId);
    },
    showChildPromoItems(parentId) {
      const index = this.expandedParentIds.indexOf(parentId);
      const parentPromo = this.promos.find(p => p.promo_id === parentId);

      if (index !== -1) {
        this.expandedParentIds.splice(index, 1); // ปิด
        this.selectedParentTitle = null; // เคลียร์ชื่อ
      } else {
        this.expandedParentIds = [parentId]; // เปิดแค่ตัวเดียว
        this.selectedParentTitle = parentPromo?.title || null; // ตั้งชื่อโปรโมชันแม่
      }
    }

  },
};
</script>


<style scoped>
</style>
